import React, { useState, useEffect, useRef } from 'react';
import { User, LogOut, ChevronDown, RefreshCw } from 'lucide-react';
import { UserProfile } from '../user/UserProfile';
import CountrySelector from '../ui/CountrySelector';

// El encabezado ya no busca. Cada página tiene su propio buscador, que además
// entiende su catálogo —tiendas, departamentos, tallas—, mientras que el del
// encabezado sólo sabía consultar «Explorar»: desde Viajes o Exclusivos
// escribir ahí sacaba al usuario de donde estaba sin avisarle.
export const Header = ({
  user,
  onLoginClick,
  onLogout,
  updateUserProfile,
  country,
  onCountryChange,
  onActualizarApp,
  onInstalarApp,
}) => {
  const [showUserProfile, setShowUserProfile] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowUserDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <>
      <header id="header-principal" style={{ background: '#0a0a0a', borderBottom: '1px solid #1e1e1e' }} className="sticky top-0 z-50">
        <div id="header-contenedor" className="header-contenedor" style={{ maxWidth: '1440px', margin: '0 auto' }}>
          <div id="header-barra" className="header-barra" style={{ display: 'flex', alignItems: 'center' }}>

            {/* Logo */}
            <a id="header-logo" href="/" style={{ display: 'flex', alignItems: 'center', gap: '10px', textDecoration: 'none', flexShrink: 0 }}>
              <img
                src="/logo-cholloman.png"
                alt="CholloMan"
                style={{ width: 36, height: 36, objectFit: 'contain' }}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <span style={{
                fontSize: '1.25rem',
                fontWeight: 800,
                letterSpacing: '-0.04em',
                color: '#ffffff',
                lineHeight: 1
              }}>
                Chollo<span style={{ color: '#FF4500' }}>Man</span>
              </span>
            </a>

            {/* Right section */}
            <div id="header-acciones" style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>

              {/* Country selector */}
              {onCountryChange && (
                <CountrySelector country={country} onCountryChange={onCountryChange} />
              )}

              {/* Auth */}
              {user ? (
                <div id="header-usuario-menu" ref={dropdownRef} style={{ position: 'relative' }}>
                  <button
                    id="header-usuario-boton"
                    onClick={() => setShowUserDropdown(v => !v)}
                    className="boton-usuario"
                    aria-label={`Menú de ${user.username}`}
                    aria-expanded={showUserDropdown}
                  >
                    {/* Sólo la inicial. El nombre estaba también acá, recortado
                        a 90px con puntos suspensivos, y se repite entero —con el
                        correo— en cuanto se despliega el menú: en la barra
                        ocupaba sitio para decir a medias algo que está completo
                        un clic más allá.

                        `aria-label` lo conserva para quien no ve el icono: la
                        inicial sola no dice de quién es la cuenta. */}
                    <span className="boton-usuario__inicial" aria-hidden="true">
                      {(user.username || 'U').charAt(0).toUpperCase()}
                    </span>
                    <ChevronDown
                      size={14}
                      className={`boton-usuario__flecha${showUserDropdown ? ' boton-usuario__flecha--abierta' : ''}`}
                    />
                  </button>

                  {showUserDropdown && (
                    <div style={{
                      position: 'absolute',
                      right: 0,
                      top: 'calc(100% + 8px)',
                      background: '#111111',
                      border: '1px solid #2a2a2a',
                      borderRadius: '12px',
                      minWidth: 220,
                      boxShadow: '0 16px 48px rgba(0,0,0,0.5)',
                      overflow: 'hidden',
                      zIndex: 150
                    }}>
                      {/* User info */}
                      <div style={{ padding: '14px 16px', borderBottom: '1px solid #1e1e1e' }}>
                        <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#fff' }}>{user.username}</div>
                        <div style={{ fontSize: '0.75rem', color: '#666', marginTop: 2 }}>{user.email}</div>
                      </div>

                      {/* Menu items */}
                      <div style={{ padding: '6px' }}>
                        <DropdownItem
                          icon={<User size={15} />}
                          label="Mi cuenta"
                          onClick={() => { setShowUserDropdown(false); setShowUserProfile(true); }}
                        />
                        {/* Salida manual cuando algo se ve desactualizado: en el
                            celular no hay forma de forzar una recarga completa. */}
                        <DropdownItem
                          icon={<RefreshCw size={15} />}
                          label="Actualizar app"
                          onClick={() => { setShowUserDropdown(false); onActualizarApp?.(); }}
                        />
                       {/* <DropdownItem
                          icon={<Bell size={15} />}
                          label="Mi seguimiento"
                          onClick={() => { setShowUserDropdown(false); setShowUserProfile('tracking'); }}
                        />*/}
                      </div>

                      <div style={{ borderTop: '1px solid #1e1e1e', padding: '6px' }}>
                        <DropdownItem
                          icon={<LogOut size={15} />}
                          label="Cerrar sesión"
                          danger
                          onClick={() => { setShowUserDropdown(false); onLogout(); }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <button
                  id="header-login-boton"
                  onClick={onLoginClick}
                  className="boton boton--naranja boton--pastilla"
                >
                  Entrar
                </button>
              )}
            </div>
          </div>

        </div>

      </header>

      {/* User Profile Panel */}
      {showUserProfile && (
        <UserProfile
          user={user}
          onClose={() => setShowUserProfile(false)}
          onUpdateUser={updateUserProfile}
          initialSection={showUserProfile === 'tracking' ? 'tracking' : 'profile'}
          country={country}
          onInstalarApp={() => { setShowUserProfile(false); onInstalarApp?.(); }}
        />
      )}
    </>
  );
};

const DropdownItem = ({ icon, label, onClick, danger }) => (
  <button
    onClick={onClick}
    className={`menu-item${danger ? ' menu-item--peligro' : ''}`}
  >
    {icon}
    {label}
  </button>
);
